import { fetchIRAndAssetsDetails } from '@/actions/fetchProduct';
import AssetDetailCard from '@/components/Asset/AssetDetailCard';
import NoDeviceCard from '@/components/Device/NoDeviceCard';
import PageLayout from '@/components/Layouts/PageLayout';
import Map from '@/components/Map';
import { INSTALLATION_REQUEST } from '@/constants/amc';
import { DEVICE_STATUS } from '@/constants/app';
import { useProductContext } from '@/context/productContext';
import useGTM from '@/hooks/useGTM';
import useOnMount from '@/hooks/useOnMount';
import useTriggerRequest from '@/hooks/useTriggerRequest';
import Button from '@/shared/Button';
import Container from '@/shared/Container';
import Div from '@/shared/Div';
import Paragraph from '@/shared/Paragraph';
import { GTMEventNames, SCREEN_NAMES } from '@/types/gtm';
import { getAddress } from '@/utils/address';
import { blockAMCJourney } from '@/utils/amcPlans';
import is from '@/utils/is';
import {
  getQueryParam,
  hasQueryParam,
  hasQueryParamWithValue,
  removeQueryParam,
} from '@/utils/url';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ContentLoader from 'react-content-loader';

const Loader = (props: any) => (
  <ContentLoader
    speed={2}
    width={320}
    height={500}
    viewBox="0 0 320 500"
    backgroundColor="#f3f3f3"
    foregroundColor="#ecebeb"
    className="w-full sm:w-fit h-fit m-auto"
    {...props}
  >
    <rect x="0" y="0" rx="5" ry="5" width="320" height="100" />
    <rect x="0" y="120" rx="5" ry="5" width="320" height="100" />
    <rect x="0" y="240" rx="5" ry="5" width="320" height="100" />
    <rect x="0" y="360" rx="5" ry="5" width="320" height="100" />
  </ContentLoader>
);

function DevicesList() {
  const { state: productState, dispatch } = useProductContext();
  const [assetsData, setAssetsData] = useState({
    assetsDetailsData: productState?.list || [],
    irDetailsData: productState?.irList || [],
  });
  const fetch = (...args: any) => fetchIRAndAssetsDetails.apply(null, args);
  const { data, promisifyTrigger, isLoading, isError, isSuccess } = useTriggerRequest({
    fetch,
  });
  const [noDevice, setNoDevice] = useState(false);
  const router = useRouter();

  const { fireGTMEvent } = useGTM();
  useOnMount(async () => {
    fireGTMEvent(GTMEventNames.LOGGEDIN_DEVICE_HOMEPAGE_LANDED, {
      screen_name: SCREEN_NAMES.DEVICE_HOMEPAGE,
    });
    let assetsData = productState?.list;
    if (is.empty.array(assetsData) || is.empty.array(productState?.irList)) {
      const { assets, installationRequests } = await promisifyTrigger();
      setAssetsData({
        assetsDetailsData: assets,
        irDetailsData: installationRequests,
      });
      if (is.empty.array(assets) && is.empty.array(installationRequests)) {
        setNoDevice(true);
      }
      dispatch.setProductsList(assets);
      dispatch.setIRDetails(installationRequests);
      assetsData = assets;
    }
    const assetId = 'assetid';
    const planPage = 'planpage';
    const hasAssetIdQuery = hasQueryParam(assetId);
    const assetIdQueryValue = getQueryParam(assetId);
    const isPlanPage = hasQueryParamWithValue(planPage, 'YES');
    let selectedAsset: any = {};
    if (assetsData?.length === 1) {
      dispatch.setProductsData(assetsData?.[0]);
      selectedAsset = assetsData?.[0];
    } else if (hasAssetIdQuery) {
      const matchedAsset = assetsData.find((asset: any) => asset.id === assetIdQueryValue);
      if (matchedAsset) {
        selectedAsset = matchedAsset;
        dispatch.setProductsData(matchedAsset);
      }
    }
    removeQueryParam(assetId);
    removeQueryParam(planPage);
    if (
      selectedAsset &&
      isPlanPage &&
      (assetsData?.length === 1 || hasAssetIdQuery) &&
      !blockAMCJourney(selectedAsset)
    ) {
      router.push(`/amc-plans/${selectedAsset.id}/${selectedAsset.deviceId}`);
    }
  });

  const renderLoaderOrErrorOrNoDevice = useCallback(() => {
    if (noDevice) {
      return <NoDeviceCard />;
    } else if (isLoading) return <Loader />;
    else if (isError)
      return (
        <Div className="flex flex-col gap-s6 border rounded-3xl p-s6 justify-center items-center">
          <Paragraph className="text-h-1">Something went wrong!</Paragraph>
          <Button className="w-fit" onClick={() => window.location.reload()}>
            Try again!
          </Button>
        </Div>
      );
    return null;
  }, [isLoading, isError]);

  const renderInstallationRequests = useCallback((data: any) => {
    const {
      houseNo,
      address1,
      address2,
      city,
      stateOrProvince,
      pincode,
      product,
      msdyn_servicerequest,
    } = data || {};
    const { image, name } = product || {};
    const address = {
      address1,
      address2,
      house_no: houseNo,
      city,
      postal_code: pincode,
      state: stateOrProvince,
    };

    return (
      <AssetDetailCard
        address={getAddress(address)}
        productImage={image}
        isIR={true}
        productName={name}
        woStatus={INSTALLATION_REQUEST}
        // cohortStatus={DEVICE_STATUS.IW}
        ticketnumber={msdyn_servicerequest?.ticketnumber}
      />
    );
  }, []);

  const renderAssetsDetails = useCallback((selectedAssetData: any) => {
    const {
      img,
      id,
      address,
      name,
      expiryStatus: { status },
      planType,
      workOrderDetails,
      isEFSApplicable,
    } = selectedAssetData || {};

    let cohortStatus = '';
    if (isEFSApplicable && status !== DEVICE_STATUS.IC && status !== DEVICE_STATUS.IW)
      cohortStatus = DEVICE_STATUS.EFS;
    else {
      cohortStatus =
        status === DEVICE_STATUS.OC || status === DEVICE_STATUS.OW || status === DEVICE_STATUS.IW
          ? status
          : planType;
    }
    return (
      <AssetDetailCard
        address={getAddress(address)}
        assetId={id}
        productImage={img}
        productName={name}
        isIR={false}
        woStatus={workOrderDetails?.workOrderSystemStatus}
        cohortStatus={cohortStatus}
        workOrderDetails={workOrderDetails}
        selectedAssetData={selectedAssetData}
      />
    );
  }, []);

  return (
    <PageLayout hideFooter={true}>
      <Container className="md:max-w-xl pt-s9 pb-s7">
        {isLoading || isError || noDevice ? (
          renderLoaderOrErrorOrNoDevice()
        ) : (
          <Div className="flex flex-col gap-s6">
            {is.empty.array(assetsData?.irDetailsData) ? null : (
              <Div className="flex flex-col gap-s4">
                <Paragraph className="text-h-3 md:text-hd-3">Installation Scheduled</Paragraph>
                <Map list={assetsData?.irDetailsData} render={renderInstallationRequests} />
              </Div>
            )}
            {is.empty.array(assetsData?.assetsDetailsData) ? null : (
              <Div className="flex flex-col gap-s4">
                <Paragraph className="text-h-3 md:text-hd-3">All Devices</Paragraph>
                <Map
                  className="flex flex-col gap-s5"
                  list={assetsData?.assetsDetailsData}
                  render={renderAssetsDetails}
                />
              </Div>
            )}
          </Div>
        )}
      </Container>
    </PageLayout>
  );
}

export default DevicesList;
